import React, { useState } from 'react';
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
  Paper,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { marketAPI } from '../../services/api';

const CreateMarket = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    market_name: '',
    open_time: '09:00:00',
    close_time: '10:00:00',
    // Result Settings - Added these
    open_digit: '',
    close_digit: '',
    open_panna: '',
    close_panna: '',
    // Status settings
    market_status: 'active',
    market_available: true,
    market_active: true,
    schedule: {
      monday: { active: true, open_time: '', close_time: '' },
      tuesday: { active: true, open_time: '', close_time: '' },
      wednesday: { active: true, open_time: '', close_time: '' },
      thursday: { active: true, open_time: '', close_time: '' },
      friday: { active: true, open_time: '', close_time: '' },
      saturday: { active: true, open_time: '', close_time: '' },
      sunday: { active: true, open_time: '', close_time: '' },
    }
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    setError('');
    setSuccess('');
  };

  const handleScheduleChange = (day, field, value) => {
    setFormData({
      ...formData,
      schedule: {
        ...formData.schedule,
        [day]: {
          ...formData.schedule[day],
          [field]: value
        }
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Prepare schedule data
      const scheduleData = {};
      days.forEach(day => {
        scheduleData[day] = {
          active: formData.schedule[day].active,
          open_time: formData.schedule[day].open_time || formData.open_time,
          close_time: formData.schedule[day].close_time || formData.close_time
        };
      });

      const marketData = {
        ...formData,
        schedule: scheduleData,
        // Convert empty strings to null for result fields
        open_digit: formData.open_digit ? parseInt(formData.open_digit) : null,
        close_digit: formData.close_digit ? parseInt(formData.close_digit) : null,
        open_panna: formData.open_panna ? parseInt(formData.open_panna) : null,
        close_panna: formData.close_panna ? parseInt(formData.close_panna) : null,
      };

      await marketAPI.createMarket(marketData);
      setSuccess('Market created successfully!');
      setTimeout(() => {
        navigate('/dashboard/markets');
      }, 2000);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to create market');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Typography variant="h4" gutterBottom>
        Create New Market
      </Typography>

      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
                
                <TextField
                  fullWidth
                  label="Market Name"
                  name="market_name"
                  value={formData.market_name}
                  onChange={handleChange}
                  margin="normal"
                  required
                  disabled={loading}
                />

                <Typography variant="subtitle1" sx={{ mt: 3, mb: 2 }}>
                  Timing
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Open Time"
                      name="open_time"
                      type="time"
                      value={formData.open_time}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                      required
                      disabled={loading}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Close Time"
                      name="close_time"
                      type="time"
                      value={formData.close_time}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                      required
                      disabled={loading}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

         

          {/* Status Settings */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Status Settings
                </Typography>

                <FormControl fullWidth margin="normal">
                  <InputLabel>Market Status</InputLabel>
                  <Select
                    name="market_status"
                    value={formData.market_status}
                    onChange={handleChange}
                    label="Market Status"
                    disabled={loading}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>

                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Multiple Selection Options:
                </Typography>

                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="market_available"
                        checked={formData.market_available}
                        onChange={handleChange}
                        disabled={loading}
                      />
                    }
                    label="Market Available"
                  />
                </Box>

                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="market_active"
                        checked={formData.market_active}
                        onChange={handleChange}
                        disabled={loading}
                      />
                    }
                    label="Market Active"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Market Schedule */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Market Schedule
                </Typography>
                
                <Grid container spacing={2}>
                  {days.map((day) => (
                    <Grid item xs={12} sm={6} md={4} key={day}>
                      <Paper sx={{ p: 2, backgroundColor: 'rgba(255,255,255,0.05)' }}>
                        <Typography variant="subtitle2" sx={{ textTransform: 'capitalize', mb: 1 }}>
                          {day}
                        </Typography>
                        
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.schedule[day].active}
                              onChange={(e) => handleScheduleChange(day, 'active', e.target.checked)}
                              disabled={loading}
                            />
                          }
                          label="Active"
                        />

                        {formData.schedule[day].active && (
                          <Box mt={1}>
                            <Grid container spacing={1}>
                              <Grid item xs={6}>
                                <TextField
                                  fullWidth
                                  label="Open"
                                  type="time"
                                  size="small"
                                  value={formData.schedule[day].open_time}
                                  onChange={(e) => handleScheduleChange(day, 'open_time', e.target.value)}
                                  InputLabelProps={{ shrink: true }}
                                  disabled={loading}
                                />
                              </Grid>
                              <Grid item xs={6}>
                                <TextField
                                  fullWidth
                                  label="Close"
                                  type="time"
                                  size="small"
                                  value={formData.schedule[day].close_time}
                                  onChange={(e) => handleScheduleChange(day, 'close_time', e.target.value)}
                                  InputLabelProps={{ shrink: true }}
                                  disabled={loading}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Submit Buttons */}
          <Grid item xs={12}>
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={() => navigate('/dashboard/markets')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{ minWidth: 120 }}
              >
                {loading ? 'Creating...' : 'Create Market'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </motion.div>
  );
};

export default CreateMarket;
